import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthStore } from '@/shared/session'
import { Button, Input } from '@/shared/ui'
import { hintId } from '@/shared/lib'
import PasswordRecoveryDialog from './PasswordRecoveryDialog'

/**
 * Where PocketBase's mailed links land.
 *
 * Three of them, and they exist because the delivery mechanism changed rather
 * than because the app grew: Supabase put a magic link in the URL hash and
 * fired an event the app listened for, so a token never appeared in a path.
 * PocketBase mails `{APP_URL}/<thing>/{TOKEN}`, so each one needs somewhere to
 * arrive. The templates are repointed in the schema migration, away from
 * PocketBase's own admin UI, which is where they point out of the box and where
 * a person has no account.
 */

/** `/reset/:token`. Nobody is signed in; the token is the authorisation. */
export function ResetPasswordRoute() {
  const { token } = useParams<{ token: string }>()
  return <PasswordRecoveryDialog token={token ?? ''} />
}

/**
 * `/verify/:token`. Spent on arrival, because there is nothing to ask.
 *
 * `users.authRule` is empty, so an unverified account can already sign in and
 * this is housekeeping rather than a gate. It still matters: if that rule is
 * ever tightened, this is the page that lets somebody in.
 */
export function VerifyEmailRoute() {
  const confirmVerification = useAuthStore((s) => s.confirmVerification)
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    // Guarded against running twice. React's development StrictMode
    // deliberately mounts effects twice, and the second call spends a token that
    // is already gone, so without this the page reports a failure it caused.
    let spent = false
    const token = window.location.pathname.split('/verify/')[1] ?? ''
    void (async () => {
      if (spent) return
      spent = true
      const failed = await confirmVerification(token)
      if (failed) setError(failed)
      else setDone(true)
    })()
    return () => {
      spent = true
    }
  }, [confirmVerification])

  return (
    <div className="flex flex-col items-start gap-3">
      <h1 className="text-xl font-semibold">
        {done ? 'Address confirmed' : error ? 'That link did not work' : 'Confirming your address…'}
      </h1>
      {error && (
        <p className="text-sm text-muted-foreground">
          {error}. A confirmation link can only be used once, and it expires. Ask for a new one from
          Settings if you still need it.
        </p>
      )}
      {done && <p className="text-sm text-muted-foreground">Thanks. Nothing else to do.</p>}
      <Button type="button" size="sm" onClick={() => void navigate('/')}>
        Continue
      </Button>
    </div>
  )
}

/**
 * `/email/:token`. Asks for the password, because PocketBase requires it.
 *
 * Not an inconvenience to design around: the mail arrives at the NEW address,
 * so without the password a stranger who can read that inbox could move an
 * account onto it. The password is what proves the person changing the address
 * is the one who owns the account.
 */
export function ConfirmEmailChangeRoute() {
  const { token } = useParams<{ token: string }>()
  const confirmEmailChange = useAuthStore((s) => s.confirmEmailChange)
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (loading) {
      setError('Still confirming…')
      return
    }
    if (!password) {
      setError('Enter your password first.')
      return
    }
    setLoading(true)
    setError(null)
    const failed = await confirmEmailChange(token ?? '', password)
    setLoading(false)
    if (failed) {
      setError(failed)
      return
    }
    setDone(true)
  }

  if (done) {
    return (
      <div className="flex flex-col items-start gap-3">
        <h1 className="text-xl font-semibold">Address changed</h1>
        <p className="text-sm text-muted-foreground">
          Sign in again with your new address.
        </p>
        <Button type="button" size="sm" onClick={() => void navigate('/')}>
          Continue
        </Button>
      </div>
    )
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="flex max-w-sm flex-col gap-3">
      <h1 className="text-xl font-semibold">Confirm your new address</h1>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="ce-pw" className="text-xs font-medium">
          Your password
        </label>
        <p id={hintId('ce-pw')} className="text-xs text-muted-foreground">
          The mail went to the new address, so your password is what proves the account is yours.
        </p>
        <Input
          id="ce-pw"
          type="password"
          aria-describedby={hintId('ce-pw')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          disabled={loading}
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div>
        <Button type="submit" size="sm">
          {loading ? 'Confirming…' : 'Confirm'}
        </Button>
      </div>
    </form>
  )
}
